import React, { useState } from 'react';

import { Modal, Button, Form } from 'react-bootstrap';
import Select, { MultiValue } from 'react-select';

import {
  Company,
  ItemBase,
  ITEMCLASSTYPE,
  ITEMFIELDS,
  SelectOpt,
} from '@/types';
import { selectCustomStyle } from '@/config/model';
import { useAddItem, CATALOGUE_TYPE, FITTING_TYPE } from '@/features/items';

type ItemCatalogueAddModalProps = {
  show: boolean;
  activeCompanies: Company[];
  handleClose: () => void;
};

/**
 * == Add Net Item Modal ====
 * @returns
 */
const ItemCatalogueAddModal = ({
  show,
  activeCompanies,
  handleClose,
}: ItemCatalogueAddModalProps) => {
  // == defualt payload ==
  const initialPayload: ItemBase = {
    id: '',
    description: '',
    type: CATALOGUE_TYPE.F,
    category: FITTING_TYPE.D,
    ui_visible: true,
    classification_type: ITEMCLASSTYPE.S, // STANDARD | CUSTOM
    companies: [], // no companies attached to
  };
  // == form upload payload ==
  const [payload, setPayload] = useState(initialPayload);

  const companyToSelectOpt = (c: Company) => ({
    label: c.name,
    value: c.id as number,
  });
  const companyOptions = activeCompanies.map(companyToSelectOpt);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPayload((prevPayload) => ({ ...prevPayload, [name]: value }));
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    // Special case: clear companies if item is STANDARD!
    if (name == ITEMFIELDS.CL && value == ITEMCLASSTYPE.S) {
      setPayload({
        ...payload,
        companies: [], // clear `companies` if
        [ITEMFIELDS.CL]: ITEMCLASSTYPE.S,
      });
      return;
    }
    setPayload((prevPayload) => ({ ...prevPayload, [name]: value }));
  };

  const handleCompanySelect = (values: MultiValue<SelectOpt>) => {
    const companies = values.map((o) => o.value);
    setPayload({ ...payload, companies });
  };

  // query client to add item:
  const addItem = useAddItem({
    onSuccess: () => {
      handleClose();
      clearPayload();
    },
  });
  const handleSubmitToAddNewItem = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    await addItem.mutateAsync({ payload });
  };

  const clearPayload = () => {
    setPayload(initialPayload); // Reset payload to its initial state
  };

  return (
    <Modal
      as={Modal.Dialog}
      centered
      show={show}
      onHide={() => {
        handleClose();
        clearPayload();
      }}
    >
      <Modal.Header>
        <Modal.Title className="h6">Add Item</Modal.Title>
        <Button
          variant="close"
          aria-label="Close"
          onClick={() => {
            handleClose();
            clearPayload();
          }}
        />
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmitToAddNewItem}>
          {/* == ITEM ID IN CAPITALIZED FORMAT == */}
          <Form.Group className="mb-3">
            <Form.Label>Item ID (like: BALL_VALVE)</Form.Label>
            <Form.Control
              value={payload.id || ''}
              name={ITEMFIELDS.I}
              placeholder="capitalize & no space, or use _ to connect words"
              onChange={handleInputChange}
            />
          </Form.Group>
          {/* == Item description == */}
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              value={payload.description || ''}
              name={ITEMFIELDS.D}
              onChange={handleInputChange}
            />
          </Form.Group>
          {/* == Item Classifcation */}
          <Form.Group className="mb-3">
            <Form.Label>
              Item Classification(if Customized for company)
            </Form.Label>
            <Form.Select
              value={payload.classification_type || ITEMCLASSTYPE.S}
              name={ITEMFIELDS.CL}
              onChange={handleSelectChange}
            >
              <option value={ITEMCLASSTYPE.S}>Standard</option>
              <option value={ITEMCLASSTYPE.C}>Custom</option>
            </Form.Select>
          </Form.Group>
          {/* == Company selection == */}
          {payload.classification_type == ITEMCLASSTYPE.C && (
            <Select
              isMulti
              placeholder="Select a company..."
              name={ITEMFIELDS.CO}
              options={companyOptions}
              classNamePrefix="select"
              styles={selectCustomStyle}
              onChange={handleCompanySelect}
            />
          )}
          {/* == Item type: FITTING|LENGTH|... */}
          <Form.Group className="mb-3 mt-3">
            <Form.Label>Type</Form.Label>
            <Form.Select
              value={payload.type || CATALOGUE_TYPE.F}
              name={ITEMFIELDS.T}
              onChange={handleSelectChange}
            >
              {Object.values(CATALOGUE_TYPE).map((option) => (
                <option value={option} key={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {/* == Item Category: DUCT|PIPE|EQUIPMENT */}
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Select
              value={payload.category || FITTING_TYPE.D}
              name={ITEMFIELDS.CA}
              onChange={handleSelectChange}
            >
              {Object.values(FITTING_TYPE).map((option) => (
                <option value={option} key={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {/* == Item Visibility == */}
          <Form.Group className="mb-3">
            <Form.Label>Visible to UI</Form.Label>
            <Form.Select
              value={`${payload.ui_visible}`}
              name="ui_visible"
              onChange={handleSelectChange}
            >
              <option value="true">True(show)</option>
              <option value="false">False(hidden)</option>
            </Form.Select>
          </Form.Group>
          {/* === button action row === */}
          <div className="d-flex justify-content-end gap-3">
            <Button
              variant="light"
              onClick={() => {
                handleClose();
                clearPayload();
              }}
            >
              Cancel
            </Button>
            <Button variant="secondary" type="submit">
              Add Item
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ItemCatalogueAddModal;
